import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Instagram } from "lucide-react";
import { RevealLine, SectionReveal, StaggerWords, ParallaxImage } from "../components/Motion";
import { useT } from "../i18n/LanguageContext";

const HERO_VIDEO_SOURCES = [
  // Silk in motion — écru/off-white silk fabric moving in slow motion.
  // Self-hosted for reliability; Mixkit CDN as CDN backup.
  "/hero-silk.mp4",
  "https://assets.mixkit.co/videos/17149/17149-720.mp4",
];

// ── VIDEO HERO ────────────────────────────────────────────────────────────
const VideoHero = () => {
  const { t } = useT();
  const ref = React.useRef(null);
  const videoRef = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const opacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.6, 0]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section
      ref={ref}
      data-testid="video-hero"
      className="relative hero-full min-h-[640px] w-full overflow-hidden bg-[#F4EFE7]"
    >
      <motion.div style={{ scale }} className="absolute inset-0">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "contrast(0.92) brightness(1.03)" }}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          data-testid="hero-video"
        >
          {HERO_VIDEO_SOURCES.map((src) => (
            <source key={src} src={src} type="video/mp4" />
          ))}
        </video>
        <div className="absolute inset-0 bg-[#4c535a]/45" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(28,28,28,0.35) 0%, rgba(28,28,28,0.0) 30%, rgba(28,28,28,0.0) 55%, rgba(28,28,28,0.55) 100%)" }} />
        <div className="grain absolute inset-0 pointer-events-none opacity-[0.06]" />
      </motion.div>

      <motion.div style={{ opacity, y }} className="relative z-10 h-full flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-6 md:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5 }}
            className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#EFECE6]/85 font-sans mb-6 md:mb-10"
          >
            Private Wardrobe Strategy
          </motion.div>

          <h1 data-testid="video-hero-title" className="font-serif text-[#EFECE6]">
            <RevealLine delay={0.6}>
              <span className="block type-display font-normal">
                <span>Ordo</span>
                <span className="ml-3 md:ml-6">Rex</span>
                <span className="ml-3 md:ml-6">Studio</span>
              </span>
            </RevealLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1.3 }}
            className="mt-12 md:mt-16 flex flex-col sm:flex-row items-center gap-5 sm:gap-8"
          >
            <Link
              to="/services"
              data-testid="video-hero-services"
              className="group inline-flex items-center gap-3 px-8 py-4 border border-[#EFECE6]/70 text-[#EFECE6] text-[11px] uppercase tracking-[0.28em] overflow-hidden relative"
            >
              <span className="absolute inset-0 bg-[#EFECE6] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              <span className="relative z-10 group-hover:text-[#4c535a] transition-colors duration-500 flex items-center gap-3">
                {t.nav.services}
                <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.4} />
              </span>
            </Link>
            <Link to="/enquiry" data-testid="video-hero-cta" className="text-[11px] uppercase tracking-[0.28em] text-[#EFECE6]/85 link-sweep">
              {t.common.begin_enquiry} →
            </Link>
          </motion.div>
        </div>

        <div className="pb-10 md:pb-12 px-6 md:px-10 flex items-end justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.6 }}
            className="text-[10px] uppercase tracking-[0.32em] text-[#EFECE6]/70 flex flex-col items-center gap-3"
          >
            <span>{t.videoHero.scroll}</span>
            <motion.span
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="block w-px h-8 bg-[#EFECE6]/70"
            />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

// ── 84% QUOTE (kinetic word-by-word) ─────────────────────────────────────
const Quote84 = () => {
  const { t } = useT();
  return (
    <section data-testid="quote-84" className="relative bg-[#EFECE6] py-20 md:py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-left">
        <h2
          className="type-h1 text-[#4c535a]"
          style={{ fontFamily: '"Traditional Arabic", "Amiri", "Cormorant Garamond", "EB Garamond", serif' }}
        >
          <StaggerWords text={t.quote84.pre} />
          <br />
          <StaggerWords text={t.quote84.italic} delay={0.2} className="italic font-light text-[#4c535a]" />
          <br />
          <StaggerWords text={t.quote84.post} delay={0.4} className="text-[#4B5158]" />
        </h2>
        <SectionReveal delay={0.6}>
          <p className="mt-8 md:mt-10 text-[11px] uppercase tracking-[0.28em] text-[#4c535a]/55">{t.quote84.credit}</p>
        </SectionReveal>
      </div>
    </section>
  );
};

// ── STUDIO ESSENCE (editorial two-column with vertical spine) ────────────
const Essence = () => {
  const { t } = useT();
  return (
    <section data-testid="essence" className="relative bg-[#4B5158] text-[#EFECE6] py-24 md:py-40 overflow-hidden">
      <div className="grain absolute inset-0 opacity-[0.06] pointer-events-none" />
      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-5 md:gap-12 items-center">
        {/* Structural spine — connects the two halves */}
        <div
          aria-hidden="true"
          data-testid="essence-spine"
          className="hidden md:block absolute inset-y-0 left-1/3 w-px bg-[#EFECE6]/20 pointer-events-none"
        />

        {/* Left column — number + eyebrow at top */}
        <div className="col-span-12 md:col-span-4 flex flex-col justify-between self-stretch md:pr-10">
          <SectionReveal>
            <p className="text-[11px] uppercase tracking-[0.36em] text-[#EFECE6]/70">
              {t.essence.number} — {t.essence.eyebrow}
            </p>
          </SectionReveal>
        </div>

        {/* Right column — heading + tight paragraph + meta */}
        <div className="col-span-12 md:col-span-8 md:pl-12 md:self-center">
          <SectionReveal>
            <h2
              className="type-h1 text-[#EFECE6] max-w-[22ch]"
              style={{ fontFamily: '"Traditional Arabic", "Amiri", "Cormorant Garamond", "EB Garamond", serif' }}
            >
              {t.essence.heading}
            </h2>
          </SectionReveal>

          <SectionReveal delay={0.15}>
            <p className="mt-10 md:mt-14 font-sans type-body text-[#EFECE6]/85 max-w-[54ch]">
              {t.essence.body}
            </p>
          </SectionReveal>

          <SectionReveal delay={0.3}>
            <div className="mt-14 md:mt-20 pt-8 border-t border-[#EFECE6]/25 flex flex-col gap-2.5 text-[11px] uppercase tracking-[0.28em] text-[#EFECE6]/70 max-w-[54ch]">
              <span>{t.essence.meta_1}</span>
              <span>{t.essence.meta_2}</span>
              <span>{t.essence.meta_3}</span>
            </div>
          </SectionReveal>
        </div>
      </div>
    </section>
  );
};

// ── THREE PILLARS (numeral-led, no icons) ───────────────────────────────
const Pillars = () => {
  const { t } = useT();
  return (
    <section data-testid="pillars" className="relative bg-[#EFECE6] py-24 md:py-40">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <p className="col-span-12 md:col-span-3 text-[11px] uppercase tracking-[0.28em] text-[#4c535a]">{t.pillars.eyebrow}</p>
          <h2 className="col-span-12 md:col-span-9 type-h1 text-[#4c535a]">
            {t.pillars.title_1}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {t.pillars.items.map((p, i) => (
            <SectionReveal key={p.key} delay={i * 0.1}>
              <Link
                to={`/services#${p.link}`}
                data-testid={`pillar-${p.key}`}
                className="group relative flex flex-col p-8 md:p-10 bg-[#E4DFD5] hover:bg-[#4c535a] transition-colors duration-700 h-full min-h-[340px]"
              >
                <h3
                  className="type-h2 text-[#4c535a] group-hover:text-[#EFECE6] transition-colors duration-500"
                  style={{ fontFamily: '"Traditional Arabic", "Amiri", "Cormorant Garamond", "EB Garamond", serif' }}
                >
                  {p.title}
                </h3>
                <p className="mt-5 type-body text-[#4c535a]/75 group-hover:text-[#EFECE6]/85 transition-colors duration-500 max-w-[36ch]">
                  {p.body}
                </p>
                <span className="mt-auto pt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#4c535a] group-hover:text-[#EFECE6] transition-colors duration-500">
                  {t.pillars.learn_more}
                </span>
              </Link>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── STUDIO TEASER ─────────────────────────────────────────────────────────
const StudioTeaser = () => {
  const { t } = useT();
  return (
    <section data-testid="studio-teaser" className="relative py-24 md:py-40 bg-[#4c535a] overflow-hidden border-t border-[#EFECE6]/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16">
          <p className="col-span-12 md:col-span-3 text-[11px] uppercase tracking-[0.28em] text-[#EFECE6]">{t.studioTeaser.eyebrow}</p>
        </div>
        <div className="grid grid-cols-12 gap-5 md:gap-8 items-center">
        <div className="col-span-12 md:col-span-5">
          <ParallaxImage
            src="https://customer-assets-agu9un31.emergentagent.net/job_wardrobe-studio-58/artifacts/ywpy34sx_ChatGPT%20Image%20Jul%2021%2C%202026%2C%2007_54_34%20PM.png"
            alt="Founder of Ordo Rex Studio in her studio"
            className="aspect-[4/5]"
            strength={60}
          />
        </div>
        <div className="col-span-12 md:col-span-6 md:col-start-7">
          <h2 className="type-h1 text-[#EFECE6]">
            <SectionReveal><span>{t.studioTeaser.title_1}</span></SectionReveal>
            <SectionReveal delay={0.1}><span className="italic font-light block">{t.studioTeaser.title_2}</span></SectionReveal>
          </h2>
          <p className="mt-8 type-body text-[#EFECE6]/80 max-w-[52ch]">{t.studioTeaser.body}</p>
          <Link
            to="/studio"
            data-testid="studio-cta"
            className="inline-flex items-center mt-8 text-[11px] uppercase tracking-[0.28em] text-[#EFECE6] whitespace-nowrap"
            style={{ gap: "0.5em" }}
          >
            <span className="link-sweep">{t.common.meet_studio}</span>
            <ArrowUpRight className="w-3.5 h-3.5 shrink-0" strokeWidth={1.4} />
          </Link>
        </div>
        </div>
      </div>
    </section>
  );
};

// ── INSTAGRAM GRID ───────────────────────────────────────────────────────
const IG_IMAGES = [
  "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1594938298603-c8148c4dae35?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=800&q=80",
];

const InstagramGrid = () => {
  const { t } = useT();
  return (
    <section data-testid="instagram-section" className="relative py-24 md:py-32 bg-[#EFECE6] border-t border-[#4c535a]/20">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-[0.36em] text-[#4c535a] mb-6">{t.instagram.eyebrow}</p>
          <h2 className="type-h1 text-[#4c535a]">
            <SectionReveal><span>{t.instagram.title_1}</span></SectionReveal>
          </h2>
          <a
            href="https://instagram.com/ordorexstudio"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="instagram-handle-link"
            className="inline-flex items-center gap-2 mt-8 text-[13px] uppercase tracking-[0.24em] text-[#4c535a]"
          >
            <Instagram className="w-4 h-4 shrink-0" strokeWidth={1.3} />
            <span className="link-sweep">{t.instagram.handle}</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// ── HOME ────────────────────────────────────────────────────────────────
export default function HomePage() {
  return (
    <>
      <VideoHero />
      <Quote84 />
      <Essence />
      <Pillars />
      <StudioTeaser />
      <InstagramGrid />
    </>
  );
}
